import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GraphqlQueryContext } from '@interfaces/graphql';
import {
  Query,
  ReportStatus,
  UserNotificationDevice,
  UserNotificationDeviceMutationsCreateArgs,
  UserProfile
} from '@interfaces/schema';

/**
 * One Signal Service.
 *
 * @exports
 * @class OneSignalService
 */
@Injectable()
export class OneSignalService {
  private readonly apollo = inject(Apollo);

  /**
   * Get profile query.
   *
   * @returns {Observable<UserProfile>}
   * @memberof OneSignalService
   */
  profile(): Observable<UserProfile> {
    const query = gql`
      {
        User {
          profile {
            id
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query })
      .pipe(map(({ data }) => get(data, 'User.profile') as UserProfile));
  }

  /**
   * Get report status query.
   *
   * @returns {Observable<ReportStatus>}
   * @memberof OneSignalService
   */
  reportStatus(): Observable<ReportStatus> {
    const query = gql`
      query reportStatus($where: ReportStatusQueryFilter) {
        Report {
          reportStatus(where: $where) {
            ANCESTRY
            DNA_360
            DNA_PREMIUM
            PRODIGY
          }
        }
      }
    `;
    return this.apollo
      .query({ query })
      .pipe(
        map(
          ({ data }) => get(data as any, 'Report.reportStatus') as ReportStatus
        )
      );
  }

  /**
   * @description Save notification click.
   * @param {{ notificationId }} [variables] Params.
   * @param {string} variables.notificationId Notification ID.
   * @returns {Observable<boolean>}
   */
  saveNotificationClick(variables?: {
    notificationId: string;
  }): Observable<boolean> {
    const mutation = gql`
      mutation MarkNotificationAsClicked($notificationId: ID!) {
        User {
          NotificationWearable {
            markAsClicked(notificationId: $notificationId) {
              success
            }
          }
        }
      }
    `;
    return this.apollo
      .mutate<{
        User: { NotificationWearable: { markAsClicked: { success: boolean } } };
      }>({ mutation, variables })
      .pipe(
        map(
          ({ data }) =>
            data?.User.NotificationWearable.markAsClicked.success as boolean
        )
      );
  }

  /**
   * @description Save notification device.
   * @param {UserNotificationDeviceMutationsCreateArgs} variables Params.
   * @param {GraphqlQueryContext} [context] GraphQL query context.
   * @returns {Observable<string>} Observable of created notification device ID.
   */
  saveNotificationDevice(
    variables: UserNotificationDeviceMutationsCreateArgs,
    context?: GraphqlQueryContext
  ): Observable<string> {
    const mutation = gql`
      mutation CreateNotificationDevice($form: UserNotificationDeviceForm) {
        User {
          Notification {
            Device {
              create(form: $form) {
                id
              }
            }
          }
        }
      }
    `;

    return this.apollo
      .mutate<{
        User: { Notification: { Device: { create: UserNotificationDevice } } };
      }>({ mutation, variables, context })
      .pipe(
        map(({ data }) => data?.User.Notification.Device.create.id as string)
      );
  }
}
